import {
  PaymentMethodType,
  isCard,
  isDigitalWallet,
  isOffSessionUsageSupported,
} from "@circle-react/helpers/paywallCheckoutHelpers";
import type { StripeElementsUpdateOptions } from "./types";
import { stripeElementsSetupFutureUsages } from "./types";

export const resolveStripeElementsPaymentMethodTypes = (
  isLinkCapabilityActive: boolean,
  paymentMethodType: string,
): StripeElementsUpdateOptions["payment_method_types"] => {
  // We have observed that Stripe Elements reports 'link' payments as 'card' `payment_method_type`.
  //
  // This function provides the `payment_method_types` used by the `useOnSubmitWithPMC` hook to
  // *temporarily* reconfigure the Stripe Elements component immediately before client-side
  // validation and payment confirmation.
  //
  // If a user selects Link (which Elements then reports as 'card'), and if we were to only pass
  // ['card'] to `elements.update()`, the Stripe Elements component would be temporarily reconfigured
  // to *only* support card payments. Consequently, the subsequent `elements.submit()` call (which
  // performs client-side validations) would fail. This is because 'link' would no longer be an
  // allowed payment method within the reconfigured Elements, despite the user's intent to use Link.
  //
  // To prevent this failure, when the reported `paymentMethodType` is 'card' (which includes
  // actual card payments and Link payments reported as 'card'), we return `[PaymentMethodType.CARD,
  // PaymentMethodType.LINK]`. This ensures that during the temporary reconfiguration by
  // `useOnSubmitWithPMC`, both 'card' and 'link' are considered allowed payment method types by
  // the Stripe Elements component. This allows `elements.submit()` and the subsequent payment
  // confirmation to succeed for both scenarios.
  //
  // *Important:*
  // We have only been able to reproduce this issue when in `subscription` mode and using Link as
  // the payment method. It does not seem to be reproducible in `payment` and `setup` modes.
  if (isCard(paymentMethodType)) {
    return isLinkCapabilityActive
      ? [PaymentMethodType.CARD, PaymentMethodType.LINK]
      : [PaymentMethodType.CARD];
  }
  // Apple and Google Pay are valid payment methods but cannot be included as options when updating
  // the Elements component because they are included under the "card" capability.
  if (isDigitalWallet(paymentMethodType)) {
    return [PaymentMethodType.CARD];
  }
  return [paymentMethodType];
};

/**
 * Resolves the `customerSessionClientSecret` for the Stripe Elements component during the
 * temporary reconfiguration in `useOnSubmitWithPMC`.
 *
 * When an existing member with saved payment methods chooses to add a *new card* (i.e.,
 * `isCard(paymentMethodType)` is true, `isMemberWithSavedPaymentMethods` is true, and
 * `isMemberUsingSavedPaymentMethod` is `false`), we temporarily remove the
 * `customerSessionClientSecret` by returning `null`.
 *
 * This is necessary because if a `customerSessionClientSecret` is provided during the
 * `elements.update()` call in `useOnSubmitWithPMC` (which reconfigures the Payment Element
 * before submission), the Payment Element tends to revert to displaying the list of saved
 * payment methods when a card is the selected payment method. This behavior would override
 * the user's input for the *new card* details.
 *
 * By setting `customerSessionClientSecret` to `null` in this specific scenario (new card
 * addition by a member with existing saved methods), we ensure that the Payment Element,
 * upon reconfiguration, defaults to showing the new card form instead of the saved payment
 * methods. This allows the `elements.submit()` call and subsequent payment confirmation to
 * correctly process the newly entered card details.
 *
 * In all other cases (e.g., non-card payment methods, no existing saved methods, or when
 * the member *is* using a saved payment method), the original `customerSessionClientSecret`
 * is passed through.
 */
export const resolveStripeElementsCustomerSessionClientSecret = (
  customerSessionClientSecret: string | undefined,
  paymentMethodType: string,
  isMemberWithSavedPaymentMethods: boolean,
  isMemberUsingSavedPaymentMethod: boolean,
) =>
  isCard(paymentMethodType) &&
  isMemberWithSavedPaymentMethods &&
  !isMemberUsingSavedPaymentMethod
    ? null
    : customerSessionClientSecret;

export const resolveStripeElementsSetupFutureUsage = (
  paymentMethodType: string,
) =>
  isOffSessionUsageSupported(paymentMethodType)
    ? stripeElementsSetupFutureUsages.off_session
    : null;
